import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, RotateCcw } from "lucide-react";
import { SOLUTION_OPTIONS } from "@/lib/site";
import Reveal from "@/components/Reveal";

export default function SolutionFinder() {
  const [selected, setSelected] = useState(null);
  const option = SOLUTION_OPTIONS.find((o) => o.key === selected);

  return (
    <section className="border-y border-charcoal/10 bg-mist py-20 text-navy md:py-28" data-testid="solution-finder-section">
      <div className="shell">
        <Reveal className="max-w-3xl">
          <p className="eyebrow text-forest">Solution finder</p>
          <h2 className="mt-4 font-serif text-3xl font-light leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
            What would you like to improve?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-charcoal/70">
            Select a goal and we’ll point you toward the solution most likely to help.
            No contact information required.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-3" role="group" aria-label="Your goals">
            {SOLUTION_OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                aria-pressed={selected === o.key}
                data-testid={`solution-chip-${o.key}`}
                onClick={() => setSelected((k) => (k === o.key ? null : o.key))}
                className={`min-h-[48px] border px-5 py-2.5 text-sm transition-all duration-300 ${
                  selected === o.key
                    ? "border-forest bg-forest font-semibold text-ivory shadow-lift"
                    : "border-forest/30 bg-white/60 text-charcoal/80 hover:border-forest hover:bg-white"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          {option && (
            <motion.div
              key={option.key}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 border border-charcoal/10 bg-white text-charcoal shadow-soft"
              data-testid="solution-finder-recommendation"
            >
              <div className="grid gap-8 p-8 md:grid-cols-[1fr_auto] md:items-center md:p-10">
                <div>
                  <p className="eyebrow text-river">Our recommendation</p>
                  <p className="mt-4 max-w-2xl text-base leading-relaxed md:text-lg">
                    {option.recommendation}
                  </p>
                </div>
                <div className="flex flex-col gap-3">
                  <Link to={option.interest === "become-a-partner" ? "/become-a-partner" : `/solutions/${option.service}`} className="btn-primary" data-testid="solution-finder-explore-link">
                    {option.cta}
                  </Link>
                  <Link
                    to={`/contact?interest=${option.interest}`}
                    data-testid="solution-finder-carry-link"
                    className="inline-flex min-h-[48px] items-center justify-center gap-2 px-2 text-sm font-semibold uppercase tracking-[0.14em] text-forest underline-offset-4 hover:underline"
                  >
                    Carry this into the inquiry form
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {!option && (
          <Reveal delay={0.15}>
            <p className="mt-10 flex items-center gap-3 text-sm text-charcoal/50">
              <RotateCcw className="h-4 w-4" />
              Select a goal above to see a recommendation.
            </p>
          </Reveal>
        )}
      </div>
    </section>
  );
}
